import axios from 'axios';

// Use relative URL so it works with any host/port (NAT, proxy, etc.)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          // Try to refresh the token
          const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
            refresh: refreshToken
          });
          
          const { access, refresh: newRefresh } = response.data;
          
          // Store new tokens
          localStorage.setItem('access_token', access);
          if (newRefresh) {
            localStorage.setItem('refresh_token', newRefresh);
          }
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Simple in-memory cache with TTL
const cache = new Map();
const CACHE_TTL = 30000; // 30 seconds cache

const getCached = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Clear cache for a specific resource type (call after mutations)
export const clearCache = (resourceType = null) => {
  if (resourceType) {
    for (const key of cache.keys()) {
      if (key.includes(resourceType)) {
        cache.delete(key);
      }
    }
  } else {
    cache.clear();
  }
};

// Track ongoing requests to prevent duplicates
const pendingRequests = new Map();

// Helper function to fetch all data using bulk endpoint with caching
const fetchBulk = async (endpoint, params = {}) => {
  const cacheKey = `${endpoint}:${JSON.stringify(params)}`;
  
  // Check cache first
  const cached = getCached(cacheKey);
  if (cached) {
    return { data: cached };
  }
  
  // Check if request is already pending
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }
  
  // Make the request
  const requestPromise = api.get(endpoint, { params }).then(response => {
    const data = Array.isArray(response.data) ? response.data : (response.data.results || response.data);
    setCache(cacheKey, data);
    pendingRequests.delete(cacheKey);
    return { data };
  }).catch(error => {
    pendingRequests.delete(cacheKey);
    throw error;
  });
  
  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Legacy function for paginated endpoints (if needed)
const fetchAllPages = async (endpoint, params = {}) => {
  const cacheKey = `pages:${endpoint}:${JSON.stringify(params)}`;
  
  const cached = getCached(cacheKey);
  if (cached) {
    return { data: cached };
  }
  
  let allResults = [];
  let nextUrl = endpoint;
  
  while (nextUrl) {
    const response = await api.get(nextUrl, { params });
    const data = response.data;
    
    if (data.results) {
      allResults = [...allResults, ...data.results];
      nextUrl = data.next ? data.next.replace(API_BASE_URL, '') : null;
      params = {};
    } else {
      allResults = Array.isArray(data) ? data : [data];
      nextUrl = null;
    }
  }
  
  setCache(cacheKey, allResults);
  return { data: allResults };
};

// Clients API
export const clientsAPI = {
  getAll: (params) => fetchBulk('/clients/all/', params),
  get: (id) => api.get(`/clients/${id}/`),
  create: (data) => api.post('/clients/', data).then(r => { clearCache('clients'); return r; }),
  update: (id, data) => {
    // Sanitize data - remove read-only fields and convert empty strings to null for date fields
    const sanitizedData = { ...data };
    
    // Remove read-only fields that shouldn't be sent
    delete sanitizedData.client_code;
    delete sanitizedData.created_at;
    delete sanitizedData.updated_at;
    delete sanitizedData.is_deleted;
    delete sanitizedData.id;
    delete sanitizedData.username;
    delete sanitizedData.user_email;
    delete sanitizedData.generated_password;
    delete sanitizedData.server_name;
    
    // Convert empty strings to null for date fields
    if (sanitizedData.rc_issue_date === '') {
      sanitizedData.rc_issue_date = null;
    }
    
    console.log('Client update request (sanitized):', { id, data: sanitizedData });
    return api.patch(`/clients/${id}/`, sanitizedData).then(r => { 
      console.log('Client update success:', r.data);
      clearCache('clients'); 
      return r; 
    }).catch(error => {
      console.error('Client update error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestData: sanitizedData
      });
      
      // Provide user-friendly error messages
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        let userMessage = 'Erreur lors de la mise à jour du client';
        
        if (typeof errorData === 'object') {
          const fieldErrors = Object.keys(errorData);
          if (fieldErrors.length > 0) {
            const firstField = fieldErrors[0];
            const fieldError = Array.isArray(errorData[firstField]) 
              ? errorData[firstField][0] 
              : errorData[firstField];
            userMessage = `Erreur: ${firstField} - ${fieldError}`;
          }
        }
        
        // Throw a more user-friendly error
        const userError = new Error(userMessage);
        userError.originalError = error;
        userError.status = 400;
        throw userError;
      }
      
      throw error;
    });
  },
  delete: (id) => api.delete(`/clients/${id}/`).then(r => { clearCache('clients'); return r; }),
  getVehicles: (id) => api.get(`/clients/${id}/vehicles/`),
  getDocuments: (id) => api.get(`/clients/${id}/documents/`),
  getProfile: (id) => api.get(`/clients/${id}/profile/`),
  resetPassword: (id) => api.post(`/clients/${id}/reset_password/`),
  restore: (id) => api.post(`/clients/${id}/restore/`).then(r => { clearCache('clients'); return r; }),
  getDeleted: () => api.get('/clients/deleted/'),
  arpceExport: () => api.get('/clients/arpce_export/', { responseType: 'blob' }),
};

// Devices API
export const devicesAPI = {
  getAll: (params) => api.get('/devices/', { params }),
  getAllBulk: (params) => fetchBulk('/devices/all/', params),  // Keep for other uses
  getAvailable: () => fetchBulk('/devices/all/', { status: 'stock' }),
  get: (id) => api.get(`/devices/${id}/`),
  create: (data) => api.post('/devices/', data).then(r => { clearCache('devices'); return r; }),
  update: (id, data) => api.put(`/devices/${id}/`, data).then(r => { clearCache('devices'); return r; }),
  delete: (id) => api.delete(`/devices/${id}/`).then(r => { clearCache('devices'); return r; }),
  getHistory: (deviceId) => api.get(`/devices/${deviceId}/history/`),
  getTimeline: (deviceId) => api.get(`/devices/${deviceId}/timeline/`),
  
  // Import/Export
  downloadTemplate: (format = 'xlsx') => api.get(`/devices/template/?template_format=${format}`, { responseType: 'blob' }),
  importData: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/devices/import_data/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => { 
      clearCache('devices'); 
      return r; 
    });
  },
  getImportStatus: (jobId) => api.get(`/devices/import_status/?job_id=${jobId}`),
  export: (format = 'xlsx') => api.get(`/devices/export/?export_format=${format}`, { responseType: 'blob' }),
};

// Sims API
export const simsAPI = {
  getAll: (params) => api.get('/sims/', { params }),
  get: (id) => api.get(`/sims/${id}/`),
  create: (data) => api.post('/sims/', data).then(r => { clearCache('sims'); return r; }),
  update: (id, data) => api.put(`/sims/${id}/`, data).then(r => { clearCache('sims'); return r; }),
  delete: (id) => api.delete(`/sims/${id}/`).then(r => { clearCache('sims'); return r; }),
  
  // Import/Export
  downloadTemplate: (format = 'xlsx') => api.get(`/sims/template/?template_format=${format}`, { responseType: 'blob' }),
  importData: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/sims/import_data/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => { 
      clearCache('sims'); 
      return r; 
    });
  },
  getImportStatus: (jobId) => api.get(`/sims/import_status/?job_id=${jobId}`),
  export: (format = 'xlsx') => api.get(`/sims/export/?export_format=${format}`, { responseType: 'blob' }),
};

// Providers API
export const providersAPI = {
  getAll: (params) => api.get('/providers/', { params }),
  get: (id) => api.get(`/providers/${id}/`),
  create: (data) => api.post('/providers/', data).then(r => { clearCache('providers'); return r; }),
  update: (id, data) => api.put(`/providers/${id}/`, data).then(r => { clearCache('providers'); return r; }),
  delete: (id) => api.delete(`/providers/${id}/`).then(r => { clearCache('providers'); return r; }),
};

// Vehicles API
export const vehiclesAPI = {
  getAll: (params) => fetchBulk('/vehicles/all/', params),
  getByClient: (clientId) => fetchBulk('/vehicles/all/', { client: clientId }),
  get: (id) => api.get(`/vehicles/${id}/`),
  create: (data) => api.post('/vehicles/', data).then(r => { clearCache('vehicles'); return r; }),
  update: (id, data) => api.put(`/vehicles/${id}/`, data).then(r => { clearCache('vehicles'); return r; }),
  delete: (id) => api.delete(`/vehicles/${id}/`).then(r => { clearCache('vehicles'); return r; }),
  getDeviceHistory: (vehicleId) => api.get(`/vehicles/${vehicleId}/device-history/`),
  getCoveragePeriods: (vehicleId) => api.get(`/vehicles/${vehicleId}/coverage/`),
};

// Coverage API
export const coverageAPI = {
  getHistory: (params) => fetchAllPages('/coverage-history/', params),
};

// Contracts API
export const contractsAPI = {
  getAll: (params) => fetchBulk('/contracts/all/', params),
  get: (id) => api.get(`/contracts/${id}/`),
  create: (data) => api.post('/contracts/', data).then(r => { clearCache('contracts'); return r; }),
  update: (id, data) => api.put(`/contracts/${id}/`, data).then(r => { clearCache('contracts'); return r; }),
  delete: (id) => api.delete(`/contracts/${id}/`).then(r => { clearCache('contracts'); return r; }),
  getByClient: (clientId) => fetchBulk('/contracts/all/', { client: clientId }),
  generateInvoice: (id, data) => api.post(`/contracts/${id}/generate_invoice/`, data).then(r => { clearCache('documents'); return r; }),
  getInvoices: (id) => api.get(`/contracts/${id}/invoices/`),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => fetchBulk('/documents/all/', params),
  get: (id) => api.get(`/documents/${id}/`),
  getClientBillingData: (clientId, periodStart, periodEnd) => 
    api.get('/documents/client_billing_data/', { 
      params: { client_id: clientId, period_start: periodStart, period_end: periodEnd } 
    }),
  create: (data) => api.post('/documents/', data).then(r => { clearCache('documents'); return r; }),
  update: (id, data) => api.put(`/documents/${id}/`, data).then(r => { clearCache('documents'); return r; }),
  delete: (id) => api.delete(`/documents/${id}/`).then(r => { clearCache('documents'); return r; }),
  generatePDF: (id) => api.post(`/documents/${id}/generate_pdf/`),
  download: (id) => api.get(`/documents/${id}/download/`, { responseType: 'blob' }),
  
  // Document type lists
  getProformas: () => api.get('/documents/proformas/'),
  getInvoices: () => api.get('/documents/invoices/'),
  getAttestations: () => api.get('/documents/attestations/'),
  getRecus: () => api.get('/documents/recus/'),
  getAvoirs: () => api.get('/documents/avoirs/'),
  
  // Create proforma with line items
  createProforma: (data) => api.post('/documents/create_proforma/', data).then(r => { clearCache('documents'); return r; }),
  
  // Create new document types
  createAvoir: (id) => api.post(`/documents/${id}/create_avoir/`).then(r => { clearCache('documents'); return r; }),
  
  // Edit and delete documents with PIN
  editDocument: (id, data) => api.post(`/documents/${id}/edit_document/`, data),
  deleteDocument: (id, data) => api.post(`/documents/${id}/delete_document/`, data).then(r => { clearCache('documents'); return r; }),
  
  // Update line items
  updateItems: (id, data) => api.post(`/documents/${id}/update_items/`, data).then(r => { clearCache('documents'); return r; }),
  
  // Payment
  markPaid: (id, data) => api.post(`/documents/${id}/mark_paid/`, data).then(r => { clearCache('documents'); return r; }),
  
  // Proforma workflow
  sendToClient: (id) => api.post(`/documents/${id}/send_to_client/`).then(r => { clearCache('documents'); return r; }),
  clientAccept: (id) => api.post(`/documents/${id}/client_accept/`).then(r => { clearCache('documents'); return r; }),
  clientReject: (id, reason) => api.post(`/documents/${id}/client_reject/`, { reason }).then(r => { clearCache('documents'); return r; }),
  adminValidate: (id) => api.post(`/documents/${id}/admin_validate/`).then(r => { clearCache('documents'); return r; }),
  adminReject: (id, reason) => api.post(`/documents/${id}/admin_reject/`, { reason }).then(r => { clearCache('documents'); return r; }),
  getPendingProformas: () => api.get('/documents/pending_proformas/'),
  
  // PDF Templates
  getPdfTemplate: (type) => api.get('/settings/pdf-templates/by_type/', { params: { type } }),
  
  // Duplicate/regenerate a document (for rejected invoices)
  duplicate: (id) => api.post(`/documents/${id}/duplicate/`).then(r => { clearCache('documents'); return r; }),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments/', { params }),
  get: (id) => api.get(`/payments/${id}/`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/payments/', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/payments/', data);
  },
  update: (id, data) => api.put(`/payments/${id}/`, data),
  delete: (id) => api.delete(`/payments/${id}/`),
  confirm: (id) => api.post(`/payments/${id}/confirm/`),
  cancel: (id) => api.post(`/payments/${id}/cancel/`),
  getByInvoice: (invoiceId) => api.get('/payments/', { params: { invoice: invoiceId } }),
};

// Payment Attachments API
export const paymentAttachmentsAPI = {
  getByPayment: (paymentId) => api.get('/payment-attachments/', { params: { payment: paymentId } }),
  create: (formData) => api.post('/payment-attachments/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/payment-attachments/${id}/`),
};

// Predefined Lines API
export const predefinedLinesAPI = {
  getAll: (params) => api.get('/predefined-lines/', { params }),
  get: (id) => api.get(`/predefined-lines/${id}/`),
  create: (data) => api.post('/predefined-lines/', data).then(r => { clearCache('predefined-lines'); return r; }),
  update: (id, data) => api.put(`/predefined-lines/${id}/`, data).then(r => { clearCache('predefined-lines'); return r; }),
  delete: (id) => api.delete(`/predefined-lines/${id}/`).then(r => { clearCache('predefined-lines'); return r; }),
};

// Attestation Vehicles API
export const attestationVehiclesAPI = {
  getAll: (params) => fetchAllPages('/attestation-vehicles/', params),
  create: (data) => api.post('/attestation-vehicles/', data).then(r => { clearCache('attestation'); return r; }),
  delete: (id) => api.delete(`/attestation-vehicles/${id}/`).then(r => { clearCache('attestation'); return r; }),
};

// Authentication API (uses separate axios instance to avoid interceptor loops)
const authAxios = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authAPI = {
  login: (credentials) => authAxios.post('/auth/login/', credentials),
  register: (userData) => api.post('/auth/register/', userData),  // Admin only
  logout: (data) => api.post('/auth/logout/', data),
  refresh: (data) => authAxios.post('/auth/refresh/', data),
  getProfile: () => api.get('/auth/profile/'),
  updateProfile: (data) => api.patch('/auth/profile/', data),
  changePassword: (data) => api.post('/auth/change-password/', data),
  getUsers: () => api.get('/auth/users/'),
  // Client portal endpoints
  getClientPortal: () => api.get('/auth/client-portal/'),
  getClientInvoices: () => api.get('/auth/client-portal/invoices/'),
  getClientDevices: () => api.get('/auth/client-portal/devices/'),
  getClientDocuments: () => api.get('/auth/client-portal/documents/'),
  getClientPayments: () => api.get('/auth/client-portal/payments/'),
  markDocumentViewed: (documentId) => api.post(`/auth/client-portal/documents/${documentId}/viewed/`),
  acceptDocument: (documentId) => api.post(`/auth/client-portal/documents/${documentId}/accept/`),
  rejectDocument: (documentId, reason) => api.post(`/auth/client-portal/documents/${documentId}/reject/`, { reason }),
  updateClientProfile: (data) => api.patch('/auth/client-portal/profile/', data),
};

// Settings API (public - no auth required for branding)
export const settingsAPI = {
  getBranding: () => axios.get(`${API_BASE_URL}/settings/branding/`),
  // Company settings (auth required)
  getCompanySettings: () => api.get('/settings/company/'),
  updateCompanySettings: (data) => api.put('/settings/company/', data),
  updateVisibilitySettings: (data) => api.patch('/settings/company/visibility/', data),
  getVisibilitySettings: () => api.get('/settings/company/visibility/'),
  uploadLogo: (formData) => api.post('/settings/company/upload_logo/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFavicon: (formData) => api.post('/settings/company/upload_favicon/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// PDF Templates API
export const pdfTemplatesAPI = {
  getAll: () => api.get('/settings/pdf-templates/'),
  get: (id) => api.get(`/settings/pdf-templates/${id}/`),
  getByType: (type) => api.get(`/settings/pdf-templates/by_type/?type=${type}`),
  create: (data) => api.post('/settings/pdf-templates/', data),
  update: (id, data) => api.patch(`/settings/pdf-templates/${id}/`, data),
  delete: (id) => api.delete(`/settings/pdf-templates/${id}/`),
  preview: (data) => api.post('/settings/pdf-templates/preview/', data, { responseType: 'blob' }),
};

// Document Types API (admin only for create/update/delete)
export const documentTypesAPI = {
  getAll: () => api.get('/document-types/'),
  get: (id) => api.get(`/document-types/${id}/`),
  getActive: () => api.get('/document-types/active/'),
  create: (data) => api.post('/document-types/', data),
  update: (id, data) => api.put(`/document-types/${id}/`, data),
  delete: (id) => api.delete(`/document-types/${id}/`),
};

// Client Documents API (PDF uploads)
export const clientDocumentsAPI = {
  getAll: (params) => api.get('/client-documents/', { params }),
  get: (id) => api.get(`/client-documents/${id}/`),
  getMyDocuments: () => api.get('/client-documents/my_documents/'),
  create: (formData) => api.post('/client-documents/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/client-documents/${id}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/client-documents/${id}/`),
};

// Banks API
export const banksAPI = {
  getAll: (params) => fetchBulk('/banks/', params),
  get: (id) => api.get(`/banks/${id}/`),
  create: (data) => api.post('/banks/', data).then(r => { clearCache('banks'); return r; }),
  update: (id, data) => api.patch(`/banks/${id}/`, data).then(r => { clearCache('banks'); return r; }),
  delete: (id) => api.delete(`/banks/${id}/`).then(r => { clearCache('banks'); return r; }),
};

// Checks API
export const checksAPI = {
  getAll: (params) => api.get('/checks/', { params }),
  get: (id) => api.get(`/checks/${id}/`),
  create: (formData) => api.post('/checks/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => { clearCache('checks'); return r; }),
  update: (id, formData) => api.patch(`/checks/${id}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => { clearCache('checks'); return r; }),
  delete: (id) => api.delete(`/checks/${id}/`).then(r => { clearCache('checks'); return r; }),
  linkInvoice: (id, data) => api.post(`/checks/${id}/link_invoice/`, data).then(r => { clearCache('checks'); return r; }),
  changeStatus: (id, data) => api.post(`/checks/${id}/change_status/`, data).then(r => { clearCache('checks'); return r; }),
  importStatement: (formData) => api.post('/checks/import_statement/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  reconcile: (data) => api.post('/checks/reconcile/', data).then(r => { clearCache('checks'); return r; }),
  convertFromPayment: (data) => api.post('/checks/convert_from_payment/', data).then(r => { clearCache('checks'); return r; }),
  getLegacyPayments: () => api.get('/checks/legacy_payments/'),
  getCoverageEstimate: (params) => api.get('/checks/coverage_estimate/', { params }),
  exportPDF: (params) => api.get('/checks/export_pdf/', { params, responseType: 'blob' }),
};

// Fleet Sync API
export const fleetSyncAPI = {
  // Settings
  getSettings: () => api.get('/fleet-sync/settings/'),
  updateSettings: (data) => api.patch('/fleet-sync/settings/update_settings/', data),
  triggerSync: () => api.post('/fleet-sync/settings/trigger_sync/'),
  stopSync: () => api.post('/fleet-sync/settings/stop_sync/'),
  getStatus: () => api.get('/fleet-sync/settings/status/'),
  
  // Logs
  getLogs: (params) => api.get('/fleet-sync/logs/', { params }),
  getLog: (id) => api.get(`/fleet-sync/logs/${id}/`),
  getStats: () => api.get('/fleet-sync/logs/stats/'),
  
  // Servers
  getServers: () => api.get('/fleet-sync/servers/'),
  getServer: (id) => api.get(`/fleet-sync/servers/${id}/`),
  createServer: (data) => api.post('/fleet-sync/servers/', data),
  updateServer: (id, data) => api.patch(`/fleet-sync/servers/${id}/`, data),
  deleteServer: (id) => api.delete(`/fleet-sync/servers/${id}/`),
  syncServer: (id) => api.post(`/fleet-sync/servers/${id}/sync/`),
  stopServerSync: (id) => api.post(`/fleet-sync/servers/${id}/stop_sync/`),
  testServerConnection: (id) => api.post(`/fleet-sync/servers/${id}/test_connection/`),
  getServerLiveStats: (id) => api.get(`/fleet-sync/servers/${id}/live_stats/`),


  // Vehicle Conflicts (pending/resolved/ignored)
  getConflictsSummary: () => api.get('/fleet-sync/conflicts/summary/'),
  getConflictVehicles: (params) => api.get('/fleet-sync/conflicts/conflict_vehicles/', { params }),
  getServersWithConflicts: () => api.get('/fleet-sync/conflicts/servers_with_conflicts/'),
  resolveVehicleConflict: (data) => api.post('/fleet-sync/conflicts/resolve_vehicle_conflict/', data).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),
  ignoreVehicleConflict: (vehicleId) => api.post(`/fleet-sync/conflicts/${vehicleId}/ignore_vehicle_conflict/`).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),
  reopenVehicleConflict: (vehicleId) => api.post(`/fleet-sync/conflicts/${vehicleId}/reopen_vehicle_conflict/`).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),
  clearVehicleConflict: (vehicleId) => api.post(`/fleet-sync/conflicts/${vehicleId}/clear_vehicle_conflict/`).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),
  autoResolveSingleServerConflicts: (data) => api.post('/fleet-sync/conflicts/auto_resolve_single_server/', data).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),
  getWrongClientConflicts: () => api.get('/fleet-sync/conflicts/wrong_client_conflicts/'),
  reassignToCorrectClient: (data) => api.post('/fleet-sync/conflicts/reassign_to_correct_client/', data).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),
  reassignBatch: (data) => api.post('/fleet-sync/conflicts/reassign_batch/', data).then(r => { clearCache('fleet-sync/conflicts'); clearCache('vehicles'); return r; }),

  // IMEI Conflicts
  getIMEIConflicts: (params) => api.get('/fleet-sync/imei-conflicts/', { params }),
  getIMEIConflict: (id) => api.get(`/fleet-sync/imei-conflicts/${id}/`),
  getIMEIConflictsSummary: () => api.get('/fleet-sync/imei-conflicts/summary/'),
  resolveIMEIConflictWithExisting: (id, data) => api.post(`/fleet-sync/imei-conflicts/${id}/resolve_with_existing/`, data).then(r => { clearCache('fleet-sync/imei-conflicts'); return r; }),
  resolveIMEIConflictWithNew: (id, data) => api.post(`/fleet-sync/imei-conflicts/${id}/resolve_with_new/`, data).then(r => { clearCache('fleet-sync/imei-conflicts'); return r; }),
  ignoreIMEIConflict: (id, data) => api.post(`/fleet-sync/imei-conflicts/${id}/ignore/`, data).then(r => { clearCache('fleet-sync/imei-conflicts'); return r; }),
  reopenIMEIConflict: (id) => api.post(`/fleet-sync/imei-conflicts/${id}/reopen/`).then(r => { clearCache('fleet-sync/imei-conflicts'); return r; }),
};

// Information Types API (admin only)
export const informationTypesAPI = {
  getAll: () => api.get('/information-types/'),
  get: (id) => api.get(`/information-types/${id}/`),
  create: (data) => api.post('/information-types/', data),
  update: (id, data) => api.put(`/information-types/${id}/`, data),
  delete: (id) => api.delete(`/information-types/${id}/`),
  getActive: () => api.get('/information-types/active/'),
};

// Client Information API
export const clientInformationAPI = {
  getAll: (params) => api.get('/client-information/', { params }),
  get: (id) => api.get(`/client-information/${id}/`),
  create: (data) => api.post('/client-information/', data),
  update: (id, data) => api.put(`/client-information/${id}/`, data),
  delete: (id) => api.delete(`/client-information/${id}/`),
  getMyInformation: () => api.get('/client-information/my-information/'),
  bulkUpdate: (data) => api.post('/client-information/bulk-update/', data),
};

const apiExports = {
  clientsAPI,
  devicesAPI,
  simsAPI,
  providersAPI,
  vehiclesAPI,
  coverageAPI,
  documentsAPI,
  paymentsAPI,
  paymentAttachmentsAPI,
  predefinedLinesAPI,
  attestationVehiclesAPI,
  authAPI,
  settingsAPI,
  pdfTemplatesAPI,
  fleetSyncAPI,
  documentTypesAPI,
  clientDocumentsAPI,
  informationTypesAPI,
  clientInformationAPI,
  checksAPI,
  banksAPI,
};

export default apiExports;
